import { api } from './api';

/**
 * The engine's customer documents, and the only place in the app that names
 * them. They need a bearer token (security finding #5), which a plain link,
 * `window.open` or an `<iframe src>` cannot send — so there is no URL builder
 * here on purpose: a document is identified by where it is filed, fetched with
 * the generated client, and shown from a blob URL.
 *
 * Where each one is filed (engine `PDFController`): the agreement under the
 * insured person, the letter and the broker agreement under the payer.
 */
export interface CustomerDocument {
  accountId: string;
  related: string;
}

export const agreementDocument = (
  insuredAccountId: string,
  productId: string,
): CustomerDocument => ({ accountId: insuredAccountId, related: productId });

export const letterDocument = (
  payerId: string,
  productId: string,
): CustomerDocument => ({ accountId: payerId, related: `letter-${productId}` });

export const brokerAgreementDocument = (
  payerId: string,
  orderId: string,
): CustomerDocument => ({ accountId: payerId, related: orderId });

export const isCustomerDocument = (
  value: string | CustomerDocument,
): value is CustomerDocument => typeof value !== 'string';

/** Rejects with the `Response` on a non-2xx, as the generated client does. */
export const fetchCustomerDocument = async ({
  accountId,
  related,
}: CustomerDocument): Promise<Blob> => {
  const response = await api.documentControllerGetAgreement(accountId, related);

  return response.blob();
};

/**
 * Open a document in a new tab. The tab is opened synchronously — inside the
 * click — and pointed at the blob afterwards: a `window.open` that waits for
 * the download is what popup blockers block.
 */
export const openCustomerDocument = async (
  document: CustomerDocument,
): Promise<void> => {
  const tab = window.open('', '_blank');

  try {
    const url = URL.createObjectURL(await fetchCustomerDocument(document));

    if (tab) {
      tab.location.href = url;
    }
    // The tab needs the blob for as long as it shows it; release it late.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    tab?.close();
    throw e;
  }
};
